import { useQuery } from '@tanstack/react-query';
import type { PaginatedResponse, PaginationParams } from '@/shared/types/api';

export interface RecordListParams extends PaginationParams {
  pondId?: string | null | undefined;
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
  filter?: string;
}

interface UseRecordListOptions<T, P = RecordListParams> {
  farmId: string;
  params?: P;
  queryKey: string[];
  fetchFn: (farmId: string, params?: P) => Promise<PaginatedResponse<T>>;
  enabled?: boolean;
  staleTime?: number;
}

export function useRecordList<T, P = RecordListParams>({
  farmId,
  params,
  queryKey,
  fetchFn,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutes default
}: UseRecordListOptions<T, P>) {
  const query = useQuery({
    queryKey: [...queryKey, farmId, params],
    queryFn: () => fetchFn(farmId, params),
    enabled: enabled && !!farmId,
    staleTime,
  });

  return {
    records: query.data?.data ?? [],
    pagination: query.data?.keysetPagination,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
